import {
  implantCharacteristicsSchema,
  ItemTypeEnum,
  type DeepPartial,
  type ImplantCharacteristics,
} from "@jasper/client-models";
import { ImplantCharacteristicsForm, validateSchema } from "@jasper/forms";
import { useImplantReferences } from "@jasper/use-cases";
import { Alert, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useRegisterStepSave } from "./useRegisterStepSave";

const validateSelection = ({
  value,
}: {
  value: DeepPartial<ImplantCharacteristics> | undefined;
}) => {
  const result = validateSchema(implantCharacteristicsSchema, value);

  if (!result.success) {
    return result.errors;
  }

  return undefined;
};

export const ImplantConnectionStep = ({
  products,
  orderConfiguration,
  stepKey,
  registerStepSave,
}: ProductStepProps) => {
  const { isLoading: isOrderLoading, updateProducts } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.implantConnection",
  });

  const [product] = products;

  const {
    isLoading: isImplantReferencesLoading,
    error: implantReferencesError,
    implantReferences,
  } = useImplantReferences();

  const persistedItem = product?.items.find(
    item =>
      item.type === ItemTypeEnum.abutment ||
      item.type === ItemTypeEnum.customAbutment
  );

  const defaultValues: {
    implantCharacteristics: Partial<ImplantCharacteristics>;
  } = useMemo(() => {
    if (!persistedItem || !persistedItem.implantCharacteristics) {
      return { implantCharacteristics: {} };
    }

    return {
      implantCharacteristics: persistedItem.implantCharacteristics,
    };
  }, [persistedItem]);

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const buildItems = (configuredProduct: ConfigurableProduct) =>
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        configuredProduct.items.map(({ id: _id, ...item }) => ({
          ...item,
          implantCharacteristics:
            item.type === ItemTypeEnum.abutment ||
            item.type === ItemTypeEnum.customAbutment
              ? (value.implantCharacteristics as ImplantCharacteristics)
              : undefined,
        }));

      try {
        await updateProducts(
          products.map(configuredProduct => ({
            productId: configuredProduct.id,
            items: buildItems(configuredProduct),
          }))
        );
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset(defaultValues);
    }
  }, [product, defaultValues, form]);

  if (isOrderLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">{t("productNotFound")}</Alert>
      </Stack>
    );
  }

  if (implantReferencesError) {
    return (
      <Stack p="md">
        <Alert color="red">{t("choicesUnavailable")}</Alert>
      </Stack>
    );
  }

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field
          name="implantCharacteristics"
          validators={{
            onChange: ({ value, fieldApi }) => {
              const result = validateSelection({ value });

              if (!result) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return result;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <ImplantCharacteristicsForm
                value={field.state.value}
                errors={formErrors}
                onChange={next => field.setValue(next)}
                onBlur={field.handleBlur}
                implantReferences={implantReferences}
                implantReferencesAreLoading={isImplantReferencesLoading}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
